<script>
const OUTER_HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
const INNER_HOURS = [0, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23]
const MINUTE_LABELS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]

const CLOCK_SIZE = 220
const CENTER = CLOCK_SIZE / 2
const OUTER_RADIUS = 90
const INNER_RADIUS = 62
const MINUTE_RADIUS = 90
const NUMBER_SIZE = 36
const INNER_OUTER_THRESHOLD = (OUTER_RADIUS + INNER_RADIUS) / 2

const positionOnCircle = (index, total, radius) => {
  const angle = (index / total) * 360 - 90
  const rad = (angle * Math.PI) / 180
  return {
    x: CENTER + radius * Math.cos(rad) - NUMBER_SIZE / 2,
    y: CENTER + radius * Math.sin(rad) - NUMBER_SIZE / 2,
  }
}

const angleToRadians = (deg) => (deg * Math.PI) / 180

const isInnerHour = (value) => value === 0 || value >= 13

export default {
  name: 'ClockFace',
  props: {
    mode: {
      type: String,
      default: 'hours',
      validator: (v) => ['hours', 'minutes'].includes(v),
    },
    value: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      dragging: false,
    }
  },
  computed: {
    clockSize() {
      return CLOCK_SIZE
    },
    center() {
      return CENTER
    },
    items() {
      return this.mode === 'hours' ? this._buildHourItems() : this._buildMinuteItems()
    },
    selectorAngle() {
      if (this.mode === 'hours') {
        const idx = this.value < 12 ? this.value : this.value - 12
        return (idx / 12) * 360 - 90
      }
      return (this.value / 60) * 360 - 90
    },
    selectorRadius() {
      if (this.mode === 'hours') {
        return isInnerHour(this.value) ? INNER_RADIUS : OUTER_RADIUS
      }
      return MINUTE_RADIUS
    },
    selectorEndX() {
      return CENTER + this.selectorRadius * Math.cos(angleToRadians(this.selectorAngle))
    },
    selectorEndY() {
      return CENTER + this.selectorRadius * Math.sin(angleToRadians(this.selectorAngle))
    },
  },
  beforeDestroy() {
    this._removeDocumentListeners()
  },
  methods: {
    _buildHourItems() {
      const outer = OUTER_HOURS.map((h, i) => ({
        value: h,
        label: String(h),
        ...positionOnCircle(i, 12, OUTER_RADIUS),
        isInner: false,
      }))
      const inner = INNER_HOURS.map((h, i) => ({
        value: h,
        label: String(h),
        ...positionOnCircle(i, 12, INNER_RADIUS),
        isInner: true,
      }))
      return outer.concat(inner)
    },

    _buildMinuteItems() {
      return MINUTE_LABELS.map((m, i) => ({
        value: m,
        label: String(m).padStart(2, '0'),
        ...positionOnCircle(i, 12, MINUTE_RADIUS),
        isInner: false,
      }))
    },

    isSelected(item) {
      return item.value === this.value
    },

    // ─── Pointer events (click + drag) ───────────────────
    handlePointerDown(event) {
      event.preventDefault()
      this.dragging = true
      this._resolveFromEvent(event)
      document.addEventListener('mousemove', this._onDocumentMove)
      document.addEventListener('mouseup', this._onDocumentUp)
      document.addEventListener('touchmove', this._onDocumentMove)
      document.addEventListener('touchend', this._onDocumentUp)
    },

    _onDocumentMove(event) {
      if (!this.dragging) return
      event.preventDefault()
      this._resolveFromEvent(event)
    },

    _onDocumentUp() {
      if (!this.dragging) return
      this.dragging = false
      this._removeDocumentListeners()
      this.$emit('drag-end')
    },

    _removeDocumentListeners() {
      document.removeEventListener('mousemove', this._onDocumentMove)
      document.removeEventListener('mouseup', this._onDocumentUp)
      document.removeEventListener('touchmove', this._onDocumentMove)
      document.removeEventListener('touchend', this._onDocumentUp)
    },

    _resolveFromEvent(event) {
      const clientX = event.touches ? event.touches[0].clientX : event.clientX
      const clientY = event.touches ? event.touches[0].clientY : event.clientY
      const rect = this.$refs.clockFace.getBoundingClientRect()
      const x = clientX - rect.left - CENTER
      const y = clientY - rect.top - CENTER

      let angle = Math.atan2(y, x) * (180 / Math.PI) + 90
      if (angle < 0) angle += 360

      if (this.mode === 'hours') {
        this._resolveHour(angle, x, y)
      } else {
        this._resolveMinute(angle)
      }
    },

    _resolveHour(angle, x, y) {
      const dist = Math.hypot(x, y)
      const segment = Math.round(angle / 30) % 12
      const hour = dist < INNER_OUTER_THRESHOLD
        ? INNER_HOURS[segment]
        : OUTER_HOURS[segment]
      this.$emit('input', hour)
    },

    _resolveMinute(angle) {
      const minute = Math.round(angle / 6) % 60
      this.$emit('input', minute)
    },
  },
}
</script>

<template>
  <div
    ref="clockFace"
    class="clock-face"
    :style="{ width: clockSize + 'px', height: clockSize + 'px' }"
    @mousedown="handlePointerDown"
    @touchstart="handlePointerDown"
  >
    <!-- Center dot -->
    <div class="clock-face__center" />

    <!-- Selector line -->
    <svg class="clock-face__selector" :width="clockSize" :height="clockSize">
      <line
        :x1="center"
        :y1="center"
        :x2="selectorEndX"
        :y2="selectorEndY"
        stroke="var(--primary)"
        stroke-width="2"
      />
      <circle
        :cx="selectorEndX"
        :cy="selectorEndY"
        r="16"
        fill="var(--primary)"
        opacity="0.15"
      />
    </svg>

    <!-- Numbers -->
    <div
      v-for="item in items"
      :key="mode + '-' + item.value"
      class="clock-face__number"
      :class="{
        'clock-face__number--selected': isSelected(item),
        'clock-face__number--inner': item.isInner,
      }"
      :style="{ left: item.x + 'px', top: item.y + 'px' }"
    >
      {{ item.label }}
    </div>
  </div>
</template>

<style lang="sass" scoped>
.clock-face
  position: relative
  border-radius: 50%
  background: #F5F5F5
  cursor: pointer
  user-select: none
  margin: 0 auto

.clock-face__center
  position: absolute
  top: 50%
  left: 50%
  width: 6px
  height: 6px
  border-radius: 50%
  background: var(--primary)
  transform: translate(-50%, -50%)
  z-index: 2

.clock-face__selector
  position: absolute
  top: 0
  left: 0
  pointer-events: none
  z-index: 1

.clock-face__number
  position: absolute
  width: 36px
  height: 36px
  display: flex
  align-items: center
  justify-content: center
  border-radius: 50%
  font-size: 14px
  font-weight: 500
  font-family: var(--font-family-paragraph)
  color: #333
  z-index: 2
  transition: background-color 0.15s ease, color 0.15s ease
  pointer-events: none

  &--inner
    font-size: 12px
    color: #666

  &--selected
    background: var(--primary)
    color: white
</style>
